import { arrayMove } from '@dnd-kit/sortable';
import { CurrencyDollarIcon, Edit02Icon, PlusIcon, SwitchHorizontal01Icon, Trash02Icon } from '@systemeio/icons';
import { CurrencyEnum, PaymentMethodEnum, PricePlanId, PricePlanType } from '@systemeio/types';
import { DigitalProductSection } from 'modules/digital-product/components/digital-product-section';
import { AttachPricePlanModal } from 'modules/digital-product/price-plans/components/attach-price-plan-modal';
import { EditPricePlanModal } from 'modules/digital-product/price-plans/components/edit-price-plan-modal';
import { PricePlanRow } from 'modules/digital-product/price-plans/components/price-plan-row';
import RemovePricePlanConfirmation from 'modules/digital-product/price-plans/components/remove-price-plan-confirmation';
import { SortablePricePlanRow } from 'modules/digital-product/price-plans/components/sortable-price-plan-row';
import { PricePlanRemoveTypeEnum } from 'modules/digital-product/price-plans/enums/price-plan-remove-type-enum';
import { usePricePlansApi } from 'modules/digital-product/price-plans/hooks/use-price-plans-api';
import { useUserPricePlans } from 'modules/digital-product/price-plans/hooks/use-user-price-plans';
import React, { useState } from 'react';
import { Button, FieldErrorAndDescription, SectionMessage, toast } from 'shared/components';
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch';
import Sortable from 'shared/components/sortable';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import { twJoin } from 'tailwind-merge';
interface PricePlansSectionProps {
  currency: CurrencyEnum;
  errors?: string[];
  /** Order bumps accept a single plan. */
  maxPricePlans?: number;
  /** Called when the set of attached plans changes: add, replace or remove. */
  onChange: (pricePlans: PricePlanType[]) => void;
  /**
   * Called when a plan itself was edited on the backend and the set is unchanged.
   * Defaults to `onChange`.
   */
  onPricePlanUpdated?: (pricePlans: PricePlanType[]) => void;
  paymentMethods: PaymentMethodEnum[];
  pricePlans: PricePlanType[];
  /** Renders the list without any action, e.g. a preview of a product picked elsewhere. */
  readOnly?: boolean;
  /** Lets the owner refresh its own cache after a request that changed a plan. */
  onPricePlansMutated?: (pricePlans: PricePlanType[]) => Promise<void>;
}

/**
 * Price plans of a digital product: attaching and replacing are local, creating, editing and
 * removing a plan from the account go straight to the backend.
 */
export function PricePlansSection({
  currency,
  errors,
  maxPricePlans = Infinity,
  onChange,
  onPricePlanUpdated,
  onPricePlansMutated,
  paymentMethods,
  pricePlans,
  readOnly
}: PricePlansSectionProps) {
  const {
    t
  } = useLocoTranslation();
  const {
    deletePricePlan
  } = usePricePlansApi();
  const {
    mutateUserPricePlans
  } = useUserPricePlans(currency);
  const [replacedPricePlanId, setReplacedPricePlanId] = useState<PricePlanId>();
  const [isAttachOpened, setIsAttachOpened] = useState(false);
  const [editedPricePlanId, setEditedPricePlanId] = useState<PricePlanId>();
  const [pricePlanIdToRemove, setPricePlanIdToRemove] = useState<PricePlanId | null>(null);
  const editedPricePlan = pricePlans.find(pricePlan => pricePlan.id === editedPricePlanId);
  const hasPaymentMethods = paymentMethods.length > 0;
  const canAddMore = hasPaymentMethods && pricePlans.length < maxPricePlans;
  // A single plan has nowhere to move to.
  const isSortable = !readOnly && pricePlans.length > 1;
  const openAttachModal = (replacedId?: PricePlanId) => {
    setReplacedPricePlanId(replacedId);
    setIsAttachOpened(true);
  };
  const handleSelect = (pricePlan_0: PricePlanType) => onChange(replacedPricePlanId ? pricePlans.map(el => el.id === replacedPricePlanId ? pricePlan_0 : el) : [...pricePlans, pricePlan_0]);
  const handleEdited = (pricePlan_1: PricePlanType) => {
    const updated = pricePlans.map(el_0 => el_0.id === pricePlan_1.id ? pricePlan_1 : el_0);
    (onPricePlanUpdated ?? onChange)(updated);
    onPricePlansMutated?.(updated);
  };
  const handleMove = async (activeIndex: number, overIndex: number) => onChange(arrayMove(pricePlans, activeIndex, overIndex));
  const handleRemove = async (removeType: PricePlanRemoveTypeEnum) => {
    if (!pricePlanIdToRemove) return;
    const updated_0 = pricePlans.filter(el_1 => el_1.id !== pricePlanIdToRemove);
    onChange(updated_0);
    if (removeType === PricePlanRemoveTypeEnum.from_offer) return;
    try {
      await deletePricePlan(pricePlanIdToRemove);
      await mutateUserPricePlans(data => data?.filter(el_2 => el_2.id !== pricePlanIdToRemove), false);
      await onPricePlansMutated?.(updated_0);
    } catch {
      toast.error(t('global.error'));
    }
  };
  const renderRow = (pricePlan_2: PricePlanType, sortable?: boolean) => {
    const Row = sortable ? SortablePricePlanRow : PricePlanRow;
    return <Row key={pricePlan_2.id} className={readOnly ? 'bg-bg-page' : undefined} pricePlan={pricePlan_2} actions={!readOnly && <div className={'flex items-center gap-1'}>
              <Button aria-label={t('dashboard.funnel.configuration.common.price_plan.edit_title')} onClick={() => setEditedPricePlanId(pricePlan_2.id)} variant={'gray'} size={'icon-sm'}>
                <Edit02Icon />
              </Button>
              <Button aria-label={t('dashboard.funnel.configuration.common.price_plan.change_title')} onClick={() => openAttachModal(pricePlan_2.id)} variant={'gray'} size={'icon-sm'}>
                <SwitchHorizontal01Icon />
              </Button>
              <Button aria-label={t('global.delete')} onClick={() => setPricePlanIdToRemove(pricePlan_2.id)} variant={'gray'} size={'icon-sm'}>
                <Trash02Icon />
              </Button>
            </div>} />;
  };
  return <>
      <DigitalProductSection icon={<CurrencyDollarIcon />} title={t('dashboard.funnel.configuration.offer.price_plans')} description={t('dashboard.digital_product.price_plans.description')} action={!readOnly && canAddMore && <Button aria-label={t('dashboard.funnel.configuration.offer.add_price_plan')} onClick={() => openAttachModal()} variant={'secondary'} size={'icon-md'}>
              <PlusIcon />
            </Button>}>
        <div className={twJoin('flex flex-col gap-3', readOnly && 'opacity-50')}>
          {!readOnly && !hasPaymentMethods && <SectionMessage>
              {t('dashboard.funnel.configuration.common.price_plan.payment_gateways_requirement')}{' '}
              <LinkWithoutPrefetch className={'link text-start text-sm font-normal'} href={'/profile/payment-gateways'}>
                {t('dashboard.funnel.configuration.common.price_plan.payment_gateways_requirement_link')}
              </LinkWithoutPrefetch>
            </SectionMessage>}
          {isSortable ? <Sortable items={pricePlans} draggable={pricePlan_3 => renderRow(pricePlan_3)} onMove={(_, activeIndex_0, overIndex_0) => handleMove(activeIndex_0, overIndex_0)} dragActivator="handle">
              <div className={'flex flex-col gap-2'}>
                {pricePlans.map(pricePlan_4 => renderRow(pricePlan_4, true))}
              </div>
            </Sortable> : pricePlans.map(pricePlan_5 => renderRow(pricePlan_5))}
          <FieldErrorAndDescription error={errors?.join(', ')} />
        </div>
      </DigitalProductSection>

      <AttachPricePlanModal opened={isAttachOpened} currency={currency} existingPricePlans={pricePlans} onClose={() => setIsAttachOpened(false)} onSelect={handleSelect} />
      <EditPricePlanModal opened={editedPricePlanId !== undefined} currency={currency} pricePlan={editedPricePlan} onClose={() => setEditedPricePlanId(undefined)} onEdited={handleEdited} />
      <RemovePricePlanConfirmation currency={currency} pricePlanId={pricePlanIdToRemove} onRemove={handleRemove} afterLeave={() => setPricePlanIdToRemove(null)} />
    </>;
}